/**
 * Parallax Effect Module
 *
 * Applies a parallax effect to elements with the `.parallax` class,
 * driven by vertical scroll position (via Lenis).
 * Uses IntersectionObserver to only process elements currently in the viewport.
 *
 * Configuration via data attributes:
 * - `data-parallax-speed`: Magnitude of effect (e.g. 0.1).
 * - `data-parallax-direction`: 'up' (default), 'down', 'left', or 'right'.
 * - Responsive overrides: `data-parallax-speed-md`, `data-parallax-speed-xs`, etc.
 */

import { getLenisInstance } from './scroll-manager.js';
import { getCurrentBreakpoint } from './utils/breakpoints.js';

const _reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const parallaxConfigCache     = new Map();
const activeParallaxElements   = new Set();

let lenisInstance              = null;
let observer                   = null;
let isScrollBound              = false;

function getResponsiveValue(config, property, breakpoint) {
  let value = config[property].lg;
  if ((breakpoint === 'md' || breakpoint === 'xs') && config[property].md !== null) {
    value = config[property].md;
  }
  if (breakpoint === 'xs' && config[property].xs !== null) {
    value = config[property].xs;
  }
  return value;
}

function updateParallax() {
  if (activeParallaxElements.size === 0) return;

  const breakpoint = getCurrentBreakpoint();
  const viewportHeight = window.innerHeight;

  activeParallaxElements.forEach(el => {
    const config = parallaxConfigCache.get(el);
    if (!config) return;

    const { parent } = config;
    const speed = getResponsiveValue(config, 'speed', breakpoint);
    const direction = getResponsiveValue(config, 'direction', breakpoint);

    const parentRect = parent.getBoundingClientRect();
    const parentCenter = parentRect.top + parentRect.height / 2;
    const distanceFromCenter = viewportHeight / 2 - parentCenter;

    let translateX = 0;
    let translateY = 0;

    switch (direction) {
      case 'down':
        translateY = distanceFromCenter * speed * -1;
        break;
      case 'left':
        translateX = distanceFromCenter * speed * -1;
        break;
      case 'right':
        translateX = distanceFromCenter * speed;
        break;
      case 'up':
      default:
        translateY = distanceFromCenter * speed;
        break;
    }

    el.style.transform = `translate3d(${translateX}px, ${translateY}px, 0)`;
  });
}

function bindScrollListeners() {
  if (isScrollBound || _reducedMotion.matches) return;
  if (lenisInstance) {
    lenisInstance.on('scroll', updateParallax);
  }
  window.addEventListener('resize', updateParallax);
  isScrollBound = true;
}

function unbindScrollListeners() {
  if (!isScrollBound) return;
  if (lenisInstance) {
    lenisInstance.off('scroll', updateParallax);
  }
  window.removeEventListener('resize', updateParallax);
  isScrollBound = false;
}

export function initializeParallax(container = document) {
  destroyParallax();

  if (_reducedMotion.matches) return;

  lenisInstance = getLenisInstance();
  if (!lenisInstance) {
    console.warn('Parallax: Lenis not initialized. Parallax effects will not work.');
    return;
  }

  const scope = container || document;
  const allParallaxElements = scope.querySelectorAll('.parallax');
  if (allParallaxElements.length === 0) return;

  const elements = Array.from(allParallaxElements).filter(
    el => el.parentElement?.tagName.toLowerCase() !== 'picture'
  );

  if (elements.length === 0) return;

  observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      const el = entry.target;
      if (entry.isIntersecting) {
        parallaxConfigCache.set(el, {
          parent: el.parentElement,
          speed: {
            lg: parseFloat(el.dataset.parallaxSpeed) || 0.1,
            md: el.dataset.parallaxSpeedMd !== undefined ? parseFloat(el.dataset.parallaxSpeedMd) : null,
            xs: el.dataset.parallaxSpeedXs !== undefined ? parseFloat(el.dataset.parallaxSpeedXs) : null,
          },
          direction: {
            lg: el.dataset.parallaxDirection || 'up',
            md: el.dataset.parallaxDirectionMd || null,
            xs: el.dataset.parallaxDirectionXs || null,
          }
        });

        el.style.willChange = 'transform';
        activeParallaxElements.add(el);
        updateParallax();
      } else {
        activeParallaxElements.delete(el);
        parallaxConfigCache.delete(el);

        el.style.willChange = 'auto';
        el.style.transform = 'translate3d(0, 0, 0)';
      }
    });
  }, { rootMargin: '100px 0px 100px 0px' });

  elements.forEach(el => observer.observe(el));
  bindScrollListeners();
  updateParallax();
}

export function destroyParallax() {
  unbindScrollListeners();

  if (observer) {
    activeParallaxElements.forEach(el => {
      el.style.willChange = 'auto';
      el.style.transform = '';
    });
    observer.disconnect();
    observer = null;
  }

  activeParallaxElements.clear();
  parallaxConfigCache.clear();
  lenisInstance = null;
}